import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, User, Mail, Calendar, Clock, Shield, Loader2, Building2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import PublicLayout from '../../components/layout/PublicLayout';
import DateTimePicker from '../../components/ui/DateTimePicker';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import FormField, { fieldClass } from '../../components/booking/FormField';
import PnrInput from '../../components/booking/PnrInput';
import PhoneInput from '../../components/booking/PhoneInput';
import IdProofInput from '../../components/booking/IdProofInput';
import DurationSelector from '../../components/booking/DurationSelector';
import CategorySelect from '../../components/booking/CategorySelect';
import PriceSummary from '../../components/booking/PriceSummary';
import { stationsAPI, bookingsAPI, paymentAPI, settingsAPI } from '../../services/api';
import { useUser } from '../../context/UserContext';
import {
    DEFAULT_COUNTRY_CODE,
    GUEST_NAME_MAX,
    EMAIL_MAX,
    validateBookingForm
} from '../../utils/bookingValidation';

/**
 * Customer booking form.
 *
 * Deliberately built from the same field components as the admin Create Booking modal, so
 * a guest and a counter clerk fill in an identical form with identical validation. The
 * only differences are the ones the roles genuinely require: a customer cannot pick a
 * specific room or a payment mode, and always pays online.
 */
const BookingRequest = () => {
    const { stationId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    // This route is wrapped in UserProtectedRoute, so a user is always present here.
    const { user } = useUser();

    const [station, setStation] = useState(null);
    const [categories, setCategories] = useState([]);
    const [gstInfo, setGstInfo] = useState({ percentage: 5, enabled: true });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [bookingType, setBookingType] = useState('advance');
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [availabilityData, setAvailabilityData] = useState(null);
    const [loadingAvailability, setLoadingAvailability] = useState(false);

    const [formData, setFormData] = useState({
        guestName: '',
        pnr: '',
        countryCode: DEFAULT_COUNTRY_CODE,
        mobile: '',
        email: '',
        categoryId: location.state?.selectedCategory?._id || '',
        durationSlot: '12hr',
        idCardImage: null,
        idCardNumber: '',
        idInputType: 'image'
    });
    const [errors, setErrors] = useState({});

    const patch = useCallback((changes) => {
        setFormData((prev) => ({ ...prev, ...changes }));
        setErrors((prev) => {
            const next = { ...prev };
            Object.keys(changes).forEach((key) => delete next[key]);
            if ('idCardImage' in changes || 'idCardNumber' in changes || 'idInputType' in changes) {
                delete next.idProof;
            }
            if ('mobile' in changes || 'countryCode' in changes) delete next.mobile;
            return next;
        });
    }, []);

    // Prefill from the signed-in account — the guest should not retype what we already know.
    useEffect(() => {
        if (!user) return;
        setFormData((prev) => ({
            ...prev,
            guestName: prev.guestName || user.name || '',
            email: prev.email || user.email || '',
            mobile: prev.mobile || (user.mobile ? String(user.mobile).replace(/\D/g, '').slice(-10) : '')
        }));
    }, [user]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // `/stations/:id` already returns only this station's categories, each with
                // its live room counts.
                const [stationRes, gstRes] = await Promise.all([
                    stationsAPI.getOne(stationId),
                    settingsAPI.getGST().catch(() => null)
                ]);
                if (cancelled) return;
                setStation(stationRes.data.station);
                setCategories(stationRes.data.categories || []);
                if (gstRes?.data?.gst) {
                    setGstInfo({ percentage: gstRes.data.gst.percentage, enabled: gstRes.data.gst.enabled });
                }
            } catch (error) {
                console.error('Error fetching station:', error);
                if (!cancelled) toast.error('Failed to load station details');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [stationId]);

    useEffect(() => {
        if (!selectedDate || bookingType !== 'advance') {
            setAvailabilityData(null);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoadingAvailability(true);
            try {
                const dateStr = format(selectedDate, 'yyyy-MM-dd');
                const response = await bookingsAPI.getAvailability(stationId, dateStr, formData.categoryId || undefined);
                if (!cancelled) setAvailabilityData(response.data);
            } catch (error) {
                if (!cancelled) setAvailabilityData(null);
            } finally {
                if (!cancelled) setLoadingAvailability(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedDate, bookingType, stationId, formData.categoryId]);

    const selectedCategory = categories.find((c) => c._id === formData.categoryId);
    const selectedPrice = selectedCategory
        ? (selectedCategory.pricing?.[formData.durationSlot] ?? selectedCategory.basePrice)
        : null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validateBookingForm(
            { ...formData, scheduledDate: selectedDate, scheduledTime: selectedTime },
            { requireCategory: true, requireRoom: false, requireSchedule: bookingType === 'advance' }
        );

        if (Object.keys(validationErrors).length) {
            setErrors(validationErrors);
            toast.error(Object.values(validationErrors)[0]);
            return;
        }
        setErrors({});
        setSubmitting(true);

        try {
            const data = new FormData();
            data.append('guestName', formData.guestName.trim());
            data.append('pnr', formData.pnr);
            data.append('prn', formData.pnr);
            data.append('countryCode', formData.countryCode);
            data.append('mobile', formData.mobile);
            if (formData.email.trim()) data.append('email', formData.email.trim());
            data.append('station', stationId);
            data.append('durationSlot', formData.durationSlot);
            data.append('requestedCategory', formData.categoryId);
            data.append('categoryId', formData.categoryId);

            if (formData.idInputType === 'image' && formData.idCardImage) {
                data.append('idCardImage', formData.idCardImage);
            }
            if (formData.idInputType === 'text' && formData.idCardNumber.trim()) {
                data.append('idCardNumber', formData.idCardNumber.trim());
            }
            if (bookingType === 'advance' && selectedDate && selectedTime) {
                data.append('scheduledDate', format(selectedDate, 'yyyy-MM-dd'));
                data.append('scheduledTimeSlot', selectedTime);
            }

            const bookingResponse = await bookingsAPI.createRequest(data);
            const bookingId = bookingResponse.data.booking.id;

            toast.success('Booking created! Redirecting to payment…');

            try {
                const paymentResponse = await paymentAPI.initiate(bookingId);
                if (paymentResponse.data.success && paymentResponse.data.redirectUrl) {
                    window.location.href = paymentResponse.data.redirectUrl;
                    return; // navigating away — leave `submitting` set
                }
                toast.error('Could not start the payment. You can pay from the booking status page.');
                navigate(`/booking/status/${bookingId}`);
            } catch (paymentError) {
                console.error('Payment initiation error:', paymentError);
                toast.error('Payment could not be started. You can retry from the booking status page.');
                navigate(`/booking/status/${bookingId}`);
            }
        } catch (error) {
            console.error('Error submitting booking:', error);
            const { error: message, field } = error.response?.data || {};
            // The API names the field it rejected — surface it on that input, same as admin.
            if (field) {
                const uiField = field === 'requestedCategory' ? 'categoryId' : field;
                setErrors((prev) => ({ ...prev, [uiField]: message }));
            }
            toast.error(message || 'Failed to create booking');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PublicLayout>
                <div className="min-h-[60vh] flex items-center justify-center">
                    <LoadingSpinner size="lg" />
                </div>
            </PublicLayout>
        );
    }

    const toggleClass = (active) =>
        `flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
            active
                ? 'bg-railway-500 text-white shadow-lg shadow-railway-500/30'
                : 'text-midnight-600 hover:bg-midnight-50'
        }`;

    return (
        <PublicLayout>
            <section className="bg-gradient-to-r from-midnight-950 to-midnight-900 text-white py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link
                        to={`/station/${stationId}`}
                        className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back to {station?.name}</span>
                    </Link>

                    <h1 className="text-2xl sm:text-3xl font-display font-bold">Book a Retiring Room</h1>
                    <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/10">
                        <Building2 className="w-3.5 h-3.5 text-railway-300" />
                        <span className="text-sm font-medium">
                            {station?.name}{station?.code ? ` (${station.code})` : ''}
                        </span>
                    </div>
                </div>
            </section>

            <section className="bg-midnight-50 border-b border-midnight-100">
                <div className="max-w-xl mx-auto px-4 py-4">
                    <div className="flex gap-2 bg-white rounded-xl p-1.5 shadow-sm">
                        <button
                            type="button"
                            onClick={() => setBookingType('advance')}
                            className={toggleClass(bookingType === 'advance')}
                        >
                            <Calendar className="w-4 h-4" />
                            <span>Advance Booking</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setBookingType('walkin');
                                setSelectedDate(null);
                                setSelectedTime(null);
                            }}
                            className={toggleClass(bookingType === 'walkin')}
                        >
                            <Clock className="w-4 h-4" />
                            <span>Walk-in Request</span>
                        </button>
                    </div>
                    <p className="text-center text-sm text-midnight-500 mt-2">
                        {bookingType === 'advance'
                            ? 'Book for a specific date and time in advance'
                            : 'Request a room for immediate check-in'}
                    </p>
                </div>
            </section>

            <section className="py-12 bg-gradient-to-b from-midnight-50 to-white">
                <div className="max-w-xl mx-auto px-4">
                    <div className="card">
                        <div className="card-header">
                            <h2 className="text-lg font-display font-semibold">Guest Details</h2>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4" noValidate>
                            {bookingType === 'advance' && (
                                <div>
                                    <label className="block text-sm font-medium text-midnight-700 mb-2">
                                        <Calendar className="w-4 h-4 inline mr-1" />
                                        Check-in Date &amp; Time<span className="text-red-500 ml-0.5">*</span>
                                    </label>
                                    <DateTimePicker
                                        selectedDate={selectedDate}
                                        selectedTime={selectedTime}
                                        onSelect={(date, time) => { setSelectedDate(date); setSelectedTime(time); }}
                                        onDateChange={(date) => { setSelectedDate(date); setSelectedTime(null); }}
                                        availabilityData={availabilityData}
                                        loading={loadingAvailability}
                                        maxDaysAhead={30}
                                    />
                                    {(errors.scheduledDate || errors.scheduledTime) && (
                                        <p className="mt-2 text-xs text-red-600">
                                            {errors.scheduledDate || errors.scheduledTime}
                                        </p>
                                    )}
                                </div>
                            )}

                            <FormField label="Full Name" htmlFor="guestName" required error={errors.guestName}>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-midnight-400 pointer-events-none" />
                                    <input
                                        id="guestName"
                                        type="text"
                                        value={formData.guestName}
                                        onChange={(e) => patch({ guestName: e.target.value.slice(0, GUEST_NAME_MAX) })}
                                        maxLength={GUEST_NAME_MAX}
                                        placeholder="Full name as on your ID"
                                        className={fieldClass(errors.guestName, 'pl-10 py-2.5')}
                                    />
                                </div>
                            </FormField>

                            <PnrInput
                                value={formData.pnr}
                                onChange={(pnr) => patch({ pnr })}
                                error={errors.pnr}
                            />

                            <PhoneInput
                                countryCode={formData.countryCode}
                                mobile={formData.mobile}
                                onChange={patch}
                                error={errors.mobile}
                            />

                            <FormField
                                label="Email"
                                htmlFor="email"
                                error={errors.email}
                                hint="Optional — your booking confirmation is emailed here"
                            >
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-midnight-400 pointer-events-none" />
                                    <input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => patch({ email: e.target.value.slice(0, EMAIL_MAX) })}
                                        maxLength={EMAIL_MAX}
                                        placeholder="you@example.com"
                                        className={fieldClass(errors.email, 'pl-10 py-2.5')}
                                    />
                                </div>
                            </FormField>

                            <IdProofInput
                                inputType={formData.idInputType}
                                onInputTypeChange={(idInputType) => patch({ idInputType })}
                                imageFile={formData.idCardImage}
                                onImageChange={(idCardImage) => patch({ idCardImage })}
                                idCardNumber={formData.idCardNumber}
                                onIdCardNumberChange={(idCardNumber) => patch({ idCardNumber })}
                                error={errors.idProof}
                            />

                            <DurationSelector
                                value={formData.durationSlot}
                                onChange={(durationSlot) => patch({ durationSlot })}
                                hint="Select your expected stay duration"
                            />

                            <CategorySelect
                                categories={categories}
                                value={formData.categoryId}
                                onChange={(categoryId) => patch({ categoryId })}
                                durationSlot={formData.durationSlot}
                                error={errors.categoryId}
                                stationLabel={station?.name}
                                showAvailability
                            />

                            <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-100 text-sm text-midnight-600 flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" />
                                <span>
                                    Your room number is assigned by the station when you check in. Booking
                                    reserves your chosen category for the selected time.
                                </span>
                            </div>

                            <PriceSummary
                                subtotal={selectedPrice}
                                gstPercentage={gstInfo.percentage}
                                gstEnabled={gstInfo.enabled}
                                totalLabel="Total Payable"
                                label={selectedCategory ? `${selectedCategory.name} · ${formData.durationSlot}` : undefined}
                            />

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full flex items-center justify-center gap-3 py-4 px-6 text-lg font-bold text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg hover:from-green-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {submitting ? (
                                    <><Loader2 className="w-6 h-6 animate-spin" /><span>Processing…</span></>
                                ) : (
                                    <><Shield className="w-6 h-6" /><span>Pay &amp; Book</span></>
                                )}
                            </button>

                            <div className="flex items-center justify-center gap-2 text-sm text-midnight-500">
                                <Shield className="w-4 h-4 text-green-500" />
                                <span>Secure payment via PhonePe. Your booking is confirmed only after payment.</span>
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
};

export default BookingRequest;
